<template>
  <Dialog :open="open" @update:open="handleClose" title="Import Models" size="xl">
    <div class="space-y-6">
      <div class="flex items-center justify-between">
        <h4 class="text-sm font-medium text-gray-900 dark:text-white">
          Models available in <span class="font-mono text-blue-600">{{ connectionSlug }}</span>
        </h4>
        <Button variant="outline" size="sm" @click="fetchModels" :loading="isLoading">
          Refresh List
        </Button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>

      <!-- Error State -->
      <Alert v-else-if="error" variant="error">
        {{ error }}
      </Alert>

      <!-- Models List -->
      <div v-else class="max-h-[400px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              </th>
              <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Model ID
              </th>
              <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Display Name (Optional)
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="model in availableModels" :key="model.id">
              <td class="px-4 py-3 whitespace-nowrap">
                <input type="checkbox" v-model="selectedModelIds" :value="model.id" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <code class="text-sm text-gray-900 dark:text-white">{{ model.id }}</code>
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <Input
                  v-model="modelNames[model.id]"
                  placeholder="Defaults to Model ID"
                  size="sm"
                  class="w-full max-w-xs"
                  :disabled="!selectedModelIds.includes(model.id)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Import Summary -->
      <div v-if="selectedModelIds.length > 0" class="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
        <p class="text-sm text-blue-700 dark:text-blue-300">
          Selected {{ selectedModelIds.length }} models to import as custom models.
        </p>
      </div>

      <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="outline" @click="handleClose" :disabled="isImporting">Cancel</Button>
        <Button
          variant="primary"
          @click="handleImport"
          :loading="isImporting"
          :disabled="selectedModelIds.length === 0"
        >
          Import Selected Models
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { Dialog, Button, Input, LoadingSpinner, Alert } from '@asabdev/asab-ui';
  import { useConnections } from '../composables/useConnections';
  import type { NormalisedModelItem } from '../types';
import { toUserMessage } from '@/lib/api/errors';

  interface Props {
    open: boolean;
    connectionSlug: string;
  }

  const props = defineProps<Props>();
  const emit = defineEmits(['update:open', 'imported', 'cancel']);

  const { listConnectionModels, importModels } = useConnections();

  const availableModels = ref<NormalisedModelItem[]>([]);
  const selectedModelIds = ref<string[]>([]);
  const modelNames = ref<Record<string, string>>({});
  const isLoading = ref(false);
  const isImporting = ref(false);
  const error = ref<string | null>(null);

  const isAllSelected = computed(() => {
    return availableModels.value.length > 0 && selectedModelIds.value.length === availableModels.value.length;
  });

  const toggleSelectAll = () => {
    if (isAllSelected.value) {
      selectedModelIds.value = [];
    } else {
      selectedModelIds.value = availableModels.value.map(m => m.id);
    }
  };

  const fetchModels = async () => {
    if (!props.connectionSlug) return;
    isLoading.value = true;
    error.value = null;
    try {
      const data = await listConnectionModels(props.connectionSlug);
      availableModels.value = data;
    } catch (err: unknown) {
      const message = toUserMessage(err, 'Failed to fetch models');
      error.value = message;
    } finally {
      isLoading.value = false;
    }
  };

  const handleImport = async () => {
    if (selectedModelIds.value.length === 0) return;
    isImporting.value = true;
    try {
      const payload = {
        models: selectedModelIds.value.map(id => ({
          model_id: id,
          name: modelNames.value[id]?.trim() || id
        }))
      };
      const result = await importModels(props.connectionSlug, payload);
      if (result) {
        emit('imported', result);
        handleClose();
      }
    } catch (err: unknown) {
      const message = toUserMessage(err, 'Import failed');
      error.value = message;
    } finally {
      isImporting.value = false;
    }
  };

  const handleClose = () => {
    emit('update:open', false);
    emit('cancel');
  };

  watch(() => props.open, (val) => {
    if (val) {
      selectedModelIds.value = [];
      modelNames.value = {};
      fetchModels();
    }
  });
</script>
